import { chunk } from "lodash";
import cuid from "cuid";

import type { TaskPinBoardWrite, TaskSubject, TaskSubjectWrite, TaskWrite } from "@storyboard/web-client";
import { serverNow, writeTaskPinBoards, writeTasks } from "@storyboard/web-client";
import { transport } from "@/config";
import { sendOptimisticBatch } from "@/models/sync/optimisticBatch";
import { removeTaskPinBoards, removeTasks, upsertTaskPinBoards, upsertTasks } from "@/models/task/taskStore";
import { recordEvent } from "@/services/telemetry";
import { store } from "@/store/store";
import type { TaskPinBoardRow, TaskRow } from "@/store/types";

/** The most tasks or pins one write may carry: the route's `TASK_WRITE_LIMIT`, which the wire does not export. */
export const TASK_WRITE_LIMIT = 500;

/** Who is making the tasks, in which workspace: stamped on the optimistic rows as the server stamps the stored ones. */
export type TaskWriter = { workspaceId: string; actorWorkspaceMembershipId: string | null };

/** What a dispatcher asks for before it gets ids: what the task is about, its note, and the board it is pinned to. */
export type TaskIntent = { subjects: TaskSubjectWrite[]; note: string | null; viewId: string | null };

/**
 * Mints the write for an intent: a fresh task id, and a fresh pin id when it names a board.
 *
 * Each id is its write's idempotency key and its row's identity. An empty note is no note.
 */
export function taskWriteFor(intent: TaskIntent): TaskWrite {
  const id = cuid();
  return {
    id,
    subjects: intent.subjects,
    ...(intent.note ? { note: intent.note } : {}),
    ...(intent.viewId === null ? {} : { pinBoard: creationPinOf(id, intent.viewId) }),
  };
}

/** The pin a new task carries to its board, minted as every pin is. */
function creationPinOf(taskId: string, viewId: string): TaskWrite["pinBoard"] {
  const { id } = taskPinBoardWriteFor({ taskId, viewId });
  return { id, viewId };
}

/**
 * Mints the write pinning a task to a board, which moves it there: a fresh pin id, its write's idempotency key.
 *
 * @param pin - The task and the board it moves to.
 */
export function taskPinBoardWriteFor(pin: { taskId: string; viewId: string }): TaskPinBoardWrite {
  return { id: cuid(), taskId: pin.taskId, viewId: pin.viewId };
}

/**
 * Reports a task write the server refused outright, which the page has taken back.
 *
 * @param writer - Who wrote it, and where.
 * @param kind - Whether the write held tasks or pins.
 * @param count - How many writes the failed request held.
 * @param error - What the request failed with.
 */
function reportWriteFailed(writer: TaskWriter, kind: "task" | "pin_board", count: number, error: unknown): void {
  recordEvent(
    "tasks.write_failed",
    {
      tasks: {
        workspaceId: writer.workspaceId,
        kind,
        count,
        error: error instanceof Error ? error.message : String(error),
      },
    },
    "reliability",
  );
}

/**
 * The driver a subject leads to, as the server copies it: the target itself for a driver, else the channel's or the
 * event's driver the store holds; null when it holds neither.
 */
function driverOf({ subject, targetId }: TaskSubjectWrite): string | null {
  switch (subject) {
    case "membership":
      return targetId;
    case "feed_item": {
      const feedId = store.get("item", targetId)?.feedId;
      return feedId ? (store.get("feed", feedId)?.aliasMembershipId ?? null) : null;
    }
    default:
      return store.get("tmsEvent", targetId)?.attributedMembershipId ?? null;
  }
}

/**
 * The task row the store holds until the server's copy arrives: the write, stamped as the server would stamp it.
 *
 * The instant is the server's clock as the client estimates it; the server's own `createdAt` replaces it when the
 * delta echoes the row back under the same id.
 */
export function optimisticTaskRowOf(write: TaskWrite, writer: TaskWriter): TaskRow {
  const subjects: TaskSubject[] = write.subjects.map((subject) => ({
    ...subject,
    workspaceMembershipId: driverOf(subject),
  }));
  return {
    id: write.id,
    workspaceId: writer.workspaceId,
    note: write.note ?? null,
    createdBy: writer.actorWorkspaceMembershipId,
    subjects,
    createdAt: serverNow().toISOString(),
  };
}

/** The pin row the store holds until the server's copy arrives, stamped as {@link optimisticTaskRowOf} stamps a task. */
export function optimisticPinBoardRowOf(pin: TaskPinBoardWrite, writer: TaskWriter): TaskPinBoardRow {
  return {
    id: pin.id,
    workspaceId: writer.workspaceId,
    taskId: pin.taskId,
    actorWorkspaceMembershipId: writer.actorWorkspaceMembershipId,
    viewId: pin.viewId,
    createdAt: serverNow().toISOString(),
  };
}

/** The pin a task write carries, as a pin of its own. */
function pinOfWrite(write: TaskWrite): TaskPinBoardWrite[] {
  return write.pinBoard ? [{ ...write.pinBoard, taskId: write.id }] : [];
}

/**
 * Records tasks: into the store at once, so the row's dot goes and the shelf fills on the keystroke, then to the server.
 *
 * Split at the route's batch limit. A task the server drops (its target or its board is gone) leaves the store with
 * its pin; a request that fails outright takes its whole batch back out, so the page never shows a task the log does
 * not hold.
 */
export function recordTasks(writer: TaskWriter, writes: readonly TaskWrite[]): void {
  if (writes.length === 0) return;
  upsertTasks(writes.map((write) => optimisticTaskRowOf(write, writer)));
  upsertTaskPinBoards(writes.flatMap(pinOfWrite).map((pin) => optimisticPinBoardRowOf(pin, writer)));

  for (const batch of chunk(writes, TASK_WRITE_LIMIT)) {
    // Taking a task back takes the pin it carried with it.
    const takeBack = (ids: readonly string[]) => {
      const taken = new Set(ids);
      const carried = batch.filter((write) => taken.has(write.id));
      removeTasks(ids);
      removeTaskPinBoards(carried.flatMap(pinOfWrite).map((pin) => pin.id));
    };
    sendOptimisticBatch({
      ids: batch.map((write) => write.id),
      send: () => writeTasks(transport, { workspaceId: writer.workspaceId, tasks: batch }),
      takeBack,
      onFailure: (error) => reportWriteFailed(writer, "task", batch.length, error),
    });
  }
}

/**
 * Records pins moving tasks between boards, as {@link recordTasks} records tasks.
 *
 * The route refuses a request holding two pins for one task, so only the last pin given per task is kept, written and
 * shown.
 */
export function recordTaskPinBoards(writer: TaskWriter, pins: readonly TaskPinBoardWrite[]): void {
  const latest = [...new Map(pins.map((pin) => [pin.taskId, pin])).values()];
  if (latest.length === 0) return;
  upsertTaskPinBoards(latest.map((pin) => optimisticPinBoardRowOf(pin, writer)));

  for (const pinBoards of chunk(latest, TASK_WRITE_LIMIT)) {
    sendOptimisticBatch({
      ids: pinBoards.map((pin) => pin.id),
      send: () => writeTaskPinBoards(transport, { workspaceId: writer.workspaceId, pinBoards }),
      takeBack: removeTaskPinBoards,
      onFailure: (error) => reportWriteFailed(writer, "pin_board", pinBoards.length, error),
    });
  }
}
